import React, { useEffect, useState, useRef } from "react";
import { DataGrid, gridClasses, GridMoreVertIcon } from "@mui/x-data-grid";
import {
  IconButton,
  MenuItem,
  Popover,
  Modal,
  Select,
  FormControl,
  Autocomplete,
  TextField,
} from "@mui/material";
import MatricIdsListModal from "../../components/Model/MatricIdsListModal";
import { CiEdit } from "react-icons/ci";
import { RxPlus, RxCross1 } from "react-icons/rx";
import { TbAlertHexagonFilled } from "react-icons/tb";
import { MdClose } from "react-icons/md";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { HttpService } from "../../service/HttpService";
import { toast } from "react-toastify";
import FullScreenLoader from "../../components/Loader/FullScreenLoader";
import { GoTrash } from "react-icons/go";

const ReadyToPackage = () => {
  const httpService = new HttpService();

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [cultivars, setCultivars] = useState([]);
  const [importTags, setImportTags] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);

  // Inline editing state
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});

  const [selectedActionId, setSelectedActionId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // Matric modal states
  const [matricModalOpen, setMatricModalOpen] = useState(false);
  const [selectedMatricRow, setSelectedMatricRow] = useState(null);

  const handleOpenMatricModal = (row) => {
    setSelectedMatricRow(row);
    setMatricModalOpen(true);
  };

  const editingIdRef = useRef(editingId);
  const editValuesRef = useRef(editValues);

  useEffect(() => {
    editingIdRef.current = editingId;
    editValuesRef.current = editValues;
  }, [editingId, editValues]);

  const fetchReadyToPackageData = async () => {
    setLoading(true);
    try {
      const res = await httpService.get("harvest-readytopackage");
      setPackages(res?.data || []);
    } catch (e) {
      console.error(e);
      setPackages([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchReadyToPackageData();
  }, []);

  useEffect(() => {
    const fetchImportTags = async () => {
      try {
        const res = await httpService.get("harvestimport?assigned=false");
        const list = res?.data?.data || res?.data || [];
        setImportTags(list);
      } catch (e) {
        console.error("Error fetching import tags:", e);
      }
    };
    fetchImportTags();
  }, []);

  // Handle click outside to auto-save
  useEffect(() => {
    const handleClickOutside = (event) => {
      const currentId = editingIdRef.current;
      const currentValues = editValuesRef.current;

      const isPopover = !!event.target.closest(
        ".MuiPopover-root, .MuiDialog-root, .MuiPickersPopper-root, .MuiPickersLayout-root, .MuiAutocomplete-popper, .MuiPopper-root",
      );
      if (isPopover) return;
      if (!document.body.contains(event.target)) return;

      if (currentId && currentValues && Object.keys(currentValues).length > 0) {
        const isInsideRow = event.target.closest(
          `.MuiDataGrid-row[data-id="${currentId}"], input, textarea, select, .MuiSelect-select`,
        );
        if (!isInsideRow) {
          const isScrollbarClick =
            !!event.target.closest(".MuiDataGrid-virtualScroller") &&
            !event.target.closest(".MuiDataGrid-row");
          if (isScrollbarClick) return;

          const isEmpty =
            !currentValues.harvest_date &&
            !currentValues.cultivar_id &&
            !currentValues.room_id &&
            !currentValues.source_package_tag;
          if (isEmpty) {
            handleCancelRow();
          } else {
            handleSaveRow(currentValues);
          }
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleMenuOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const handleAddRow = () => {
    const newRow = {
      id: `new_${Date.now()}`,
      week_of_harvest: "",
      harvest_date: null,
      cultivar_id: "",
      cultivar_name: "",
      room_id: "",
      room_name: "",
      plants: "",
      source_package_tag: "",
      child_package_tag: "",
      bulk_smalls_trim: "",
      weight: "",
      location: "Vault",
      isNew: true,
    };
    setPackages([newRow, ...packages]);
    setEditingId(newRow.id);
    setEditValues(newRow);
  };

  const handleEditRow = async (row) => {
    setEditingId(row.id);
    setEditValues({
      ...row,
    });
    handleMenuClose();

    if (row.harvest_date) {
      try {
        const response = await httpService.get(
          `harvest/room-cultivar?harvestedDate=${dayjs(row.harvest_date).format("YYYY-MM-DD")}`,
        );
        if (response?.data) {
          setRooms(response.data.rooms || []);
          setCultivars(response.data.cultivars || []);
        } else {
          setRooms([]);
          setCultivars([]);
        }
      } catch (error) {
        console.error("Error fetching room/cultivar data:", error);
      }
    } else {
      setRooms([]);
      setCultivars([]);
    }
  };

  const handleCancelRow = () => {
    setPackages((prev) => prev.filter((p) => !String(p.id).startsWith("new_")));
    setEditingId(null);
    setEditValues({});
    fetchReadyToPackageData();
  };

  const handleSaveRow = async (valuesToSave) => {
    const currentValues = valuesToSave || editValues;
    const currentId = editingIdRef.current || editingId;
    if (!currentValues || Object.keys(currentValues).length === 0) return;

    const isEmpty =
      !currentValues.harvest_date &&
      !currentValues.cultivar_id &&
      !currentValues.room_id &&
      !currentValues.source_package_tag;
    if (isEmpty) {
      handleCancelRow();
      return;
    }

    setLoading(true);
    try {
      const isNew = String(currentId).startsWith("new_");
      const payload = {
        week_of_harvest: parseInt(currentValues.week_of_harvest) || 0,
        harvest_date: currentValues.harvest_date
          ? dayjs(currentValues.harvest_date).format("YYYY-MM-DD")
          : null,
        cultivar_id: parseInt(currentValues.cultivar_id) || null,
        room_id: parseInt(currentValues.room_id) || null,
        plants: parseInt(currentValues.plants) || 0,
        source_package_tag: parseInt(currentValues.source_package_tag) || null,
        child_package_tag: parseInt(currentValues.child_package_tag) || null,
        bulk_smalls_trim: currentValues.bulk_smalls_trim || "",
        weight: parseFloat(currentValues.weight) || 0,
        location: currentValues.location || "Vault",
      };

      if (isNew) {
        await httpService.post("harvest-readytopackage", payload);
        toast.success("Package added successfully", { position: "top-right" });
      } else {
        await httpService.put(`harvest-readytopackage/${currentId}`, payload);
        toast.success("Package updated successfully", {
          position: "top-right",
        });
      }
      setEditingId(null);
      setEditValues({});
      fetchReadyToPackageData();
    } catch (error) {
      toast.error(
        currentValues.isNew
          ? "Failed to save package"
          : "Failed to update package",
        { position: "top-right" },
      );
    }
    setLoading(false);
  };

  const handleFieldChange = async (field, value) => {
    let updatedValues = { ...editValues, [field]: value };

    if (field === "harvest_date" && value) {
      const getWeekOfYear = (date) => {
        const d = new Date(date);
        const yearStart = new Date(d.getFullYear(), 0, 1);
        return Math.ceil(
          ((d - yearStart) / 86400000 + yearStart.getDay() + 1) / 7,
        );
      };
      const weekNumber = getWeekOfYear(value);
      updatedValues = { ...updatedValues, week_of_harvest: weekNumber };

      try {
        const response = await httpService.get(
          `harvest/room-cultivar?harvestedDate=${value}`,
        );
        if (response?.data) {
          setRooms(response.data.rooms || []);
          setCultivars(response.data.cultivars || []);
          updatedValues = {
            ...updatedValues,
            room_id: "",
            cultivar_id: "",
            plants: "",
          };
        }
      } catch (error) {
        console.error("Error fetching room/cultivar data:", error);
        setRooms([]);
        setCultivars([]);
      }
    }

    if (
      (field === "cultivar_id" || field === "room_id") &&
      updatedValues.cultivar_id &&
      updatedValues.room_id &&
      updatedValues.harvest_date
    ) {
      try {
        const response = await httpService.get(
          `harvest/plant-count?plantId=${updatedValues.cultivar_id}&roomId=${updatedValues.room_id}&harvestedDate=${updatedValues.harvest_date}`,
        );
        const count =
          response?.data?.count ?? response?.data?.data ?? response?.data;
        if (count !== undefined && count !== null) {
          updatedValues = { ...updatedValues, plants: parseInt(count) || 0 };
        }
      } catch (error) {
        console.error("Error fetching plant count:", error);
      }
    }

    setEditValues(updatedValues);
  };

  const handleDeleteRow = async () => {
    if (!selectedActionId) return;
    setLoading(true);
    try {
      await httpService.delete(
        `harvest-readytopackage/delete/${selectedActionId}`,
      );
      toast.success("Package deleted successfully", { position: "top-right" });
      setDeleteModalOpen(false);
      setSelectedActionId(null);
      fetchReadyToPackageData();
    } catch (error) {
      toast.error("Failed to delete package", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    // {
    //   field: "action",
    //   headerName: "Action",
    //   width: 100,
    //   sortable: false,
    //   disableColumnMenu: true,
    //   renderCell: (params) => (
    //     <>
    //       {editingId === params.row.id ? (
    //         <IconButton onClick={handleCancelRow} color="error">
    //           <MdClose />
    //         </IconButton>
    //       ) : (
    //         <>
    //           <IconButton onClick={(e) => handleMenuOpen(e, params.row.id)}>
    //             <GridMoreVertIcon />
    //           </IconButton>
    //           {anchorEl && menuRowId === params.row.id && (
    //             <Popover
    //               className="popup-menu"
    //               open={Boolean(anchorEl)}
    //               anchorEl={anchorEl}
    //               onClose={handleMenuClose}
    //               anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
    //               transformOrigin={{ vertical: "top", horizontal: "right" }}
    //             >
    //               <MenuItem
    //                 onClick={() => handleEditRow(params.row)}
    //                 className="menu-list-items"
    //               >
    //                 <div className="menu-list-items-text">Edit</div>
    //                 <CiEdit style={{ cursor: "pointer" }} />
    //               </MenuItem>
    //               <MenuItem
    //                 onClick={() => {
    //                   setSelectedActionId(params.row.id);
    //                   setDeleteModalOpen(true);
    //                   handleMenuClose();
    //                 }}
    //                 className="menu-list-items"
    //               >
    //                 <div className="menu-list-items-text">Delete</div>
    //                 <GoTrash style={{ cursor: "pointer" }} />
    //               </MenuItem>
    //             </Popover>
    //           )}
    //         </>
    //       )}
    //     </>
    //   ),
    // },
    {
      field: "week_of_harvest",
      headerName: "Week of Harvest",
      flex: 1,
      minWidth: 150,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <input
            type="number"
            value={editValues.week_of_harvest || ""}
            onChange={(e) =>
              handleFieldChange("week_of_harvest", e.target.value)
            }
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
          params.value || "-"
        ),
    },
    {
      field: "harvest_date",
      headerName: "Harvest Date",
      flex: 1,
      minWidth: 200,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={
                editValues.harvest_date ? dayjs(editValues.harvest_date) : null
              }
              onChange={(newValue) => {
                const dateValue = newValue ? newValue.format("YYYY-MM-DD") : "";
                handleFieldChange("harvest_date", dateValue);
              }}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
              disableFuture
            />
          </LocalizationProvider>
        ) : params.value ? (
          dayjs(params.value).format("MM-DD-YYYY")
        ) : (
          "-"
        ),
    },
    {
      field: "cultivar_name",
      headerName: "Cultivar",
      flex: 1,
      minWidth: 180,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <FormControl fullWidth size="small">
            <Select
              value={editValues.cultivar_id || ""}
              onChange={(e) => handleFieldChange("cultivar_id", e.target.value)}
              native
            >
              <option value="">Select Cultivar</option>
              {cultivars.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </FormControl>
        ) : (
          params.value || "-"
        ),
    },
    {
      field: "room_name",
      headerName: "Room",
      flex: 1,
      minWidth: 180,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <FormControl fullWidth size="small">
            <Select
              value={editValues.room_id || ""}
              onChange={(e) => handleFieldChange("room_id", e.target.value)}
              native
            >
              <option value="">Select Room</option>
              {rooms.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          </FormControl>
        ) : (
          params.value || "-"
        ),
    },
    {
      field: "plants",
      headerName: "Plants",
      flex: 1,
      minWidth: 120,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <input
            type="number"
            value={editValues.plants || ""}
            onChange={(e) => handleFieldChange("plants", e.target.value)}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
          <span
            style={{
              textDecoration: "underline",
              color: "#427fcfff",
              cursor: "pointer",
            }}
            onClick={() => handleOpenMatricModal(params.row)}
          >
            {params.value || 0}
          </span>
        ),
    },
    {
      field: "source_package_tag",
      headerName: "Source Package Tag",
      flex: 1,
      minWidth: 350,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <Autocomplete
            id="source-package-tag-autocomplete"
            options={importTags}
            getOptionLabel={(option) => {
              if (typeof option === "string") return option;
              return option?.tag || "";
            }}
            value={
              importTags.find(
                (opt) => opt.id === editValues.source_package_tag,
              ) ||
              (editValues.source_package_tag_name
                ? {
                    id: editValues.source_package_tag,
                    tag: editValues.source_package_tag_name,
                  }
                : null)
            }
            onChange={(event, newValue) => {
              setEditValues((prev) => ({
                ...prev,
                source_package_tag: newValue ? newValue.id : "",
                source_package_tag_name: newValue ? newValue.tag : "",
              }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search or Select Tag"
                variant="outlined"
                size="small"
                fullWidth
              />
            )}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                padding: "2px 8px",
              },
            }}
          />
        ) : (
          params.row.source_package_tag_name || params.value || "-"
        ),
    },
    {
      field: "child_package_tag",
      headerName: "Child Package Tag",
      flex: 1,
      minWidth: 350,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <Autocomplete
            id="child-package-tag-autocomplete"
            options={importTags}
            getOptionLabel={(option) => {
              if (typeof option === "string") return option;
              return option?.tag || "";
            }}
            value={
              importTags.find(
                (opt) => opt.id === editValues.child_package_tag,
              ) ||
              (editValues.child_package_tag_name
                ? {
                    id: editValues.child_package_tag,
                    tag: editValues.child_package_tag_name,
                  }
                : null)
            }
            onChange={(event, newValue) => {
              setEditValues((prev) => ({
                ...prev,
                child_package_tag: newValue ? newValue.id : "",
                child_package_tag_name: newValue ? newValue.tag : "",
              }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search or Select Tag"
                variant="outlined"
                size="small"
                fullWidth
              />
            )}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                padding: "2px 8px",
              },
            }}
          />
        ) : (
          params.row.child_package_tag_name || params.value || "-"
        ),
    },
    {
      field: "bulk_smalls_trim",
      headerName: "Bulk/Smalls/Trim",
      flex: 1,
      minWidth: 180,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <FormControl fullWidth size="small">
            <Select
              value={editValues.bulk_smalls_trim || ""}
              onChange={(e) =>
                handleFieldChange("bulk_smalls_trim", e.target.value)
              }
              native
            >
              <option value="">Select Type</option>
              <option value="Bulk">Bulk</option>
              <option value="Smalls">Smalls</option>
              <option value="Trim">Trim</option>
            </Select>
          </FormControl>
        ) : (
          params.value || "-"
        ),
    },
    {
      field: "weight",
      headerName: "Weight",
      flex: 1,
      minWidth: 120,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <input
            type="number"
            value={editValues.weight || ""}
            onChange={(e) => handleFieldChange("weight", e.target.value)}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : params.value ? (
          `${params.value} lbs`
        ) : (
          "-"
        ),
    },
    {
      field: "location",
      headerName: "Location",
      flex: 1,
      minWidth: 180,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <FormControl fullWidth size="small">
            <Select
              value={editValues.location || ""}
              onChange={(e) => handleFieldChange("location", e.target.value)}
              native
            >
              <option value="">Select Location</option>
              <option value="Trim Room">Trim Room</option>
              <option value="Vault">Vault</option>
            </Select>
          </FormControl>
        ) : (
          params.value || "-"
        ),
    },
  ];

  return (
    <>
      {loading && <FullScreenLoader />}
      <div className="harvest">
        <div className="page-title">
          <div className="title-wrap"></div>
        </div>

        <div className="frozen-table-wrapper" style={{ width: "100%" }}>
          {/* Fake frozen header */}
          {/* <div className="fake-frozen-header">
            <div
              className="fake-header-cell"
              style={{
                width: 100,
                backgroundColor: "#e7e7ff",
                fontFamily: "inherit",
                justifyContent: "center",
              }}
            >
              Action
            </div>
          </div> */}
          <DataGrid
            rows={packages}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            sx={{
              border: "1px solid #E0E0E0",
              [`& .${gridClasses.cell}`]: { py: 1 },
              "& .MuiDataGrid-cell[data-field='action']": {
                position: "sticky !important",
                left: "0 !important",
                zIndex: "2 !important",
                backgroundColor: "#fff !important",
                borderRight: "1px solid #E0E0E0 !important",
                boxShadow: "2px 0 5px -2px rgba(0, 0, 0, 0.2) !important",
              },
              "& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field='action']":
                {
                  backgroundColor: "#F5F5F7 !important",
                },
              "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell[data-field='action']":
                {
                  backgroundColor: "#E6F6F1 !important",
                },
            }}
            hideFooter
            showCellVerticalBorder
            showColumnVerticalBorder
          />
        </div>

        {/* Delete Modal */}
        <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
          <div className="modal_block">
            <div className="modal_header">
              <div className="modal_title_wrapper">
                <h2 className="modal_title">Are you Sure?</h2>
              </div>
              <div className="cancel-btn-wrap">
                <RxCross1 onClick={() => setDeleteModalOpen(false)} />
              </div>
            </div>
            <div className="modal_content">
              <div className="modal_content_inner">
                <div>
                  <TbAlertHexagonFilled />
                </div>
                <div>
                  <p>Do you want to delete?</p>
                </div>
              </div>
            </div>
            <div className="modal_footer">
              <button
                onClick={() => setDeleteModalOpen(false)}
                className="btn btn-border"
              >
                No
              </button>
              <button onClick={handleDeleteRow} className="btn btn-primary">
                Yes
              </button>
            </div>
          </div>
        </Modal>
        <MatricIdsListModal
          open={matricModalOpen}
          handleClose={() => {
            setMatricModalOpen(false);
            setSelectedMatricRow(null);
          }}
          row={selectedMatricRow}
        />
      </div>
    </>
  );
};

export default ReadyToPackage;
